import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { HeadingItem, Section } from '@atlaskit/menu';
import { useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import type { issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraIssue$key } from '@atlassian/jira-relay/src/__generated__/issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraIssue.graphql';
import type { issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraVersion.graphql';
import { IssueWarningProvider } from '@atlassian/jira-software-releases-version-issue-warning-controller/src/ui/index.tsx';
import messages from './messages.tsx';
import { List, ListItem, WarningDescription } from './styled.tsx';

type Props = {
	jiraIssue: issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraIssue$key;
	jiraVersion: issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraVersion$key;
};
export const IssueWarning = ({ jiraIssue, jiraVersion }: Props) => {
	const { formatMessage } = useIntl();
	const issue = useFragment(
		graphql`
			fragment issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraIssue on JiraIssue {
				...ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraIssue
			}
		`,
		jiraIssue,
	);

	const version = useFragment(
		graphql`
			fragment issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraVersion on JiraVersion {
				...ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraVersion
			}
		`,
		jiraVersion,
	);

	return (
		<IssueWarningProvider jiraVersion={version} jiraIssue={issue}>
			{({
				hasOpenPullRequestWarning,
				hasFailingBuildsWarning,
				hasUnreviewedCodeWarning,
				hasOpenReviewWarning,
			}) => {
				const warningsMessages: MessageDescriptor[] = [];
				if (hasOpenPullRequestWarning) {
					warningsMessages.push(messages.openPullRequest);
				}
				if (hasFailingBuildsWarning) {
					warningsMessages.push(messages.failingBuilds);
				}
				if (hasUnreviewedCodeWarning) {
					warningsMessages.push(messages.unreviewedCode);
				}
				if (hasOpenReviewWarning) {
					warningsMessages.push(messages.openReviews);
				}
				if (warningsMessages.length === 1) {
					return (
						<Section>
							<HeadingItem>{formatMessage(messages.issueWarningHeader)}</HeadingItem>
							<WarningDescription>
								{formatMessage(messages.issueWarning, {
									warning: formatMessage(warningsMessages[0]),
								})}
							</WarningDescription>
						</Section>
					);
				}
				if (warningsMessages.length > 1) {
					return (
						<Section>
							<HeadingItem>{formatMessage(messages.issueWarningHeader)}</HeadingItem>
							<WarningDescription>
								{formatMessage(messages.issueMultipleWarnings)}
							</WarningDescription>
							<List>
								{warningsMessages.map((warning) => (
									<ListItem key={warning.id}>{formatMessage(warning)}</ListItem>
								))}
							</List>
						</Section>
					);
				}

				return null;
			}}
		</IssueWarningProvider>
	);
};
